import { ArrowDown, ArrowUp, ArrowUpDown } from 'lucide-react';
import { Button } from '@verlo/shared/components/ui/button';
import { Signal } from '@verlo/shared/components/ui/signal';
import { allergenLabel } from '@verlo/shared/lib/allergens';
import { resolveTableState, turnStatus, sentence } from '../../lib/fohState';
import { duration, sinceWords } from '@verlo/shared/lib/duration';

// The floor as a list, one row a table: the compact view. Six to eight cards fit a tablet
// screen, so a 30-cover floor scrolled; sixteen rows fit. Same facts as the card (covers,
// stage, since, time, the allergy, the one action) in columns a server scans down.

/** The allergy as it reads on the floor: the tapped allergens, else the note. */
export function allergyWords(table) {
  if (!table?.has_allergies && !(table?.allergens || []).length) return '';
  const tapped = (table.allergens || []).map(allergenLabel).join(', ');
  return tapped ? `${tapped} allergy` : (table.allergy_notes || 'Allergy');
}

const prettyOccasion = (t) => (t?.occasion && t.occasion !== 'none'
  ? (t.occasion === 'other' && t.occasion_notes ? t.occasion_notes : t.occasion.replace(/_/g, ' ').replace(/^\w/, (c) => c.toUpperCase()))
  : '');

// Every column a fixed track or a share of what's left. The action used to be `auto`, so the
// header (no button) and each row (Open, Seat, Mark clean: three widths) shared the space out
// differently and Since, Time and Note sat a few pixels apart from their headings.
export const COMPACT_COLUMNS = 'grid grid-cols-[64px_72px_minmax(0,1.2fr)_72px_104px_minmax(0,1.6fr)_112px] items-center gap-x-3';

/** How long a seated table has been sat, and how long it has left, for sorting. A table
 *  with neither (free, reserved) sorts after every seated one, whichever way round. */
export const SORTS = {
  since: (t) => (t.status === 'occupied' && t.opened_at ? new Date(t.opened_at).getTime() : null),
  time: (t) => { const turn = turnStatus(t); return turn ? (turn.over ? -turn.minutes : turn.minutes) : null; },
};

export function sortTables(tables, sort) {
  if (!sort) return tables;
  const key = SORTS[sort.key];
  const dir = sort.dir === 'desc' ? -1 : 1;
  return [...tables].sort((a, b) => {
    const x = key(a); const y = key(b);
    if (x == null && y == null) return 0;
    if (x == null) return 1;
    if (y == null) return -1;
    return (x - y) * dir;
  });
}

function SortHeading({ id, label, sort, onSort }) {
  const on = sort?.key === id;
  const Icon = !on ? ArrowUpDown : sort.dir === 'desc' ? ArrowDown : ArrowUp;
  return (
    <button
      type="button"
      onClick={() => onSort(on && sort.dir === 'asc' ? { key: id, dir: 'desc' } : on ? null : { key: id, dir: 'asc' })}
      className={`-mx-1 flex h-7 items-center gap-1 px-1 text-left uppercase tracking-[0.06em] hover:text-ink ${on ? 'text-ink' : ''}`}
      aria-sort={on ? (sort.dir === 'desc' ? 'descending' : 'ascending') : 'none'}
      data-testid={`sort-${id}`}
    >
      {label}<Icon size={11} aria-hidden />
    </button>
  );
}

export function TableRowHeader({ sort, onSort }) {
  return (
    <div className={`${COMPACT_COLUMNS} border-b border-hairline-strong px-3.5 py-1.5 text-[10.5px] font-bold uppercase tracking-[0.06em] text-ink-faint`}>
      <span>Table</span><span>Covers</span><span>Stage</span>
      {onSort ? <SortHeading id="since" label="Since" sort={sort} onSort={onSort} /> : <span>Since</span>}
      {onSort ? <SortHeading id="time" label="Time" sort={sort} onSort={onSort} /> : <span>Time</span>}
      <span>Note</span><span />
    </div>
  );
}

export default function TableRow({ table, onOpen, onLongPressStart, onLongPressEnd }) {
  const st = resolveTableState(table);
  const status = String(table.status || '').toLowerCase();
  const occupied = status === 'occupied';
  const cleaning = status === 'cleaning';
  const turn = turnStatus(table);
  const since = occupied && table.opened_at ? sinceWords(table.opened_at) : '';
  const allergy = allergyWords(table);
  const note = allergy || prettyOccasion(table) || table.reservation?.guest_name || '';
  // A reserved table is seated (its booking, or a walk-in), not opened: same word as the card.
  const action = occupied ? 'Open' : cleaning ? 'Mark clean' : 'Seat';

  return (
    <div
      className={`${COMPACT_COLUMNS} relative h-11 cursor-pointer select-none border-b border-hairline px-3.5 text-[13.5px] last:border-b-0 hover:bg-surface-2`}
      onClick={() => onOpen(table)}
      onMouseDown={() => onLongPressStart?.(table)}
      onMouseUp={() => onLongPressEnd?.()}
      onMouseLeave={() => onLongPressEnd?.()}
      onTouchStart={() => onLongPressStart?.(table)}
      onTouchEnd={() => onLongPressEnd?.()}
      onContextMenu={(e) => e.preventDefault()}
      data-testid={`table-row-${table.name}`}
    >
      <span className={`absolute inset-y-0 left-0 w-[3px] ${st.key === 'free' ? '' : st.rail}`} aria-hidden="true" />
      <span className="font-mono font-semibold text-ink">{table.name}</span>
      <span className="font-mono tabular-nums text-ink-soft">
        {occupied && table.covers > 0 ? `${table.covers} / ${table.capacity}` : `${table.capacity} seats`}
      </span>
      <span className={`truncate ${st.key === 'free' ? 'text-ink-faint' : `font-medium ${st.text}`}`}>{sentence(st.word)}</span>
      <span className="font-mono tabular-nums text-ink-soft">{since}</span>
      <span className={turn?.over ? 'font-semibold text-signal-warn' : 'text-ink-soft'}>
        {turn ? (turn.over ? (turn.minutes >= 1 ? `${duration(turn.minutes)} over` : 'time up') : `${duration(turn.minutes)} left`) : ''}
      </span>
      <span className="min-w-0 truncate">
        {allergy
          ? <Signal tone="stop" className="whitespace-nowrap">{allergy}</Signal>
          : <span className="text-ink-soft">{note}</span>}
      </span>
      <Button
        size="sm"
        variant={occupied ? 'default' : 'outline'}
        className={`h-8 justify-self-end px-3 text-[12.5px] font-semibold ${occupied ? 'bg-verlo hover:bg-verlo-hover' : ''}`}
        onClick={(e) => { e.stopPropagation(); onOpen(table); }}
        data-testid={`table-row-action-${table.name}`}
      >
        {action}
      </Button>
    </div>
  );
}
